import React, { useEffect, useMemo, useState } from 'react';
import {
  ConversationData,
  CustomerFunnelGroup,
  dispatch,
  ErrorVariants,
  getWorkspaceId,
  showToast,
  useAddGroupMemberMutation,
  useAppSelector,
  useGetFunnelGroupsMutation,
  useLazyGetAllGroupsQuery,
  useLazyGetFunnelsQuery,
  useMoveGroupMembersMutation,
} from '@zonofi/zono-money-store';
import { ConversationUpdateModal } from '../../conversation-update-modal/conversation-update-modal';
import { ModalContentsList } from '../../modal-contents-list';
import { ModalContentItemData } from '../../../types';
import { startCase, camelCase } from 'lodash';

const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');

interface FunnelGroup extends CustomerFunnelGroup {
  funnelId: string;
  funnelName: string;
  funnelNames: string[];
}

interface LifecycleModalProps {
  open: boolean;
  onClose: () => void;
  conversation: ConversationData;
  updateConversations: (item: ConversationData) => void;
  refetchConversationList?: () => void;
}

export const LifecycleModal: React.FC<LifecycleModalProps> = ({
  open,
  onClose,
  conversation,
  updateConversations,
  refetchConversationList,
}) => {
  const safeRefetchConversationList = refetchConversationList ?? (() => {});
  const {
    channelName = '',
    title = '',
    customerLifeCyclePaths = [],
    customerId,
  } = conversation;

  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [allGroups, setAllGroups] = useState<FunnelGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutomaticByName, setIsAutomaticByName] = useState<
    Record<string, boolean>
  >({});
  const [apiCustomerGroupsById, setApiCustomerGroupsById] = useState<
    Record<
      string,
      {
        id: string;
        name: string;
        icon: string;
        isAutomatic: boolean;
        sequence: number;
      }
    >
  >({});

  const [addGroupMember] = useAddGroupMemberMutation();
  const [moveGroupMembers] = useMoveGroupMembersMutation();
  const [getFunnels] = useLazyGetFunnelsQuery();
  const [getFunnelGroups] = useGetFunnelGroupsMutation();
  const [getAllGroups] = useLazyGetAllGroupsQuery();

  // Fetch isAutomatic status from customer-group API
  useEffect(() => {
    if (!open || !sellerWorkspaceId) return;

    getAllGroups({
      sellerWorkspaceId,
      customerGroupTypes: ['CUSTOMER_LIFE_CYCLE'],
    }).then((result: any) => {
      const map: Record<string, boolean> = {};
      const groupsById: Record<
        string,
        {
          id: string;
          name: string;
          icon: string;
          isAutomatic: boolean;
          sequence: number;
        }
      > = {};
      let sequenceCounter = 1;

      result.data?.forEach((groupsResp: any) => {
        groupsResp.rootCustomerGroups?.forEach((g: any) => {
          if (g.name) map[normalize(g.name)] = g.isAutomatic;
          if (g.id && g.name) {
            groupsById[g.id] = {
              id: g.id,
              name: g.name,
              icon: g.icon ?? g.emojiId ?? '',
              isAutomatic: !!g.isAutomatic,
              sequence: sequenceCounter++,
            };
          }
        });

        const processFolders = (folders: any[]) => {
          folders?.forEach((folder) => {
            folder.customerGroups?.forEach((cg: any) => {
              if (cg.name && typeof cg.isAutomatic === 'boolean') {
                map[normalize(cg.name)] = cg.isAutomatic;
              }

              if (cg.id && cg.name) {
                groupsById[cg.id] = {
                  id: cg.id,
                  name: cg.name,
                  icon: cg.icon ?? cg.emojiId ?? '',
                  isAutomatic:
                    typeof cg.isAutomatic === 'boolean'
                      ? cg.isAutomatic
                      : false,
                  sequence: sequenceCounter++,
                };
              }
            });
            if (folder.subFolders?.length) processFolders(folder.subFolders);
          });
        };

        processFolders(groupsResp.folders);
      });

      setIsAutomaticByName(map);
      setApiCustomerGroupsById(groupsById);
    });
  }, [open, sellerWorkspaceId]);

  // Fetch funnels → groups using RTK Query
  useEffect(() => {
    if (!open || !sellerWorkspaceId) return;

    setIsLoading(true);

    const fetchGroups = async () => {
      try {
        const funnelsResult = await getFunnels({ sellerWorkspaceId }).unwrap();
        const funnels = funnelsResult?.data?.funnels ?? [];

        if (!funnels.length) return;

        const groupsResults = await Promise.allSettled(
          funnels
            .filter((f) => f.name !== 'Conversations')
            .map((funnel) =>
              getFunnelGroups({
                sellerWorkspaceId,
                params: { funnel_id: funnel.id, sellerWorkspaceId },
              })
                .unwrap()
                .then((data) => ({ funnel, groups: data?.data?.groups ?? [] }))
            )
        );

        const flat: FunnelGroup[] = [];
        groupsResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            const { funnel, groups } = result.value;
            groups.forEach((g) => {
              flat.push({
                ...g,
                funnelId: funnel.id,
                funnelName: funnel.name,
                funnelNames: [funnel.name],
              });
            });
          }
        });

        // Deduplicate by group id, merging funnel names
        const groupMap = new Map<string, FunnelGroup>();
        flat.forEach((g) => {
          if (groupMap.has(g.id)) {
            const existing = groupMap.get(g.id)!;
            if (!existing.funnelNames.includes(g.funnelName)) {
              existing.funnelNames.push(g.funnelName);
            }
          } else {
            groupMap.set(g.id, { ...g });
          }
        });

        const dedupedGroups = Array.from(groupMap.values());

        // Override group_type with isAutomatic from customer-group API
        dedupedGroups.forEach((g) => {
          const apiInfo = apiCustomerGroupsById[g.id];
          const isAutomatic =
            apiInfo?.isAutomatic ?? isAutomaticByName[normalize(g.name)];

          if (typeof isAutomatic === 'boolean') {
            g.group_type = isAutomatic ? 'automatic' : 'manual';
          }

          // If funnel groups endpoint is missing icon, fall back to emoji/icon from API.
          if (!g.icon && apiInfo?.icon) {
            g.icon = apiInfo.icon;
          }
        });

        // Merge in any lifecycle groups missing from funnel-groups API.
        // This ensures the modal shows *all* manual groups available in customer-group API.
        const existingIds = new Set(dedupedGroups.map((g) => g.id));
        const mergedGroups: FunnelGroup[] = [...dedupedGroups];

        Object.values(apiCustomerGroupsById).forEach((cg) => {
          if (existingIds.has(cg.id)) return;

          mergedGroups.push({
            id: cg.id,
            name: cg.name,
            sequence: cg.sequence,
            color: '#595959',
            icon: cg.icon,
            group_type: cg.isAutomatic ? 'automatic' : 'manual',
            funnelId: '',
            funnelName: '',
            funnelNames: [],
          });
        });

        setAllGroups(mergedGroups);
      } catch (err) {
        console.error('[LifecycleModal] fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroups();
  }, [open, sellerWorkspaceId, isAutomaticByName, apiCustomerGroupsById]);

  const groupByNormalizedName = useMemo(() => {
    const map: Record<string, FunnelGroup> = {};
    allGroups.forEach((g) => {
      map[normalize(g.name)] = g;
    });
    return map;
  }, [allGroups]);

  const currentGroups: FunnelGroup[] = useMemo(
    () =>
      customerLifeCyclePaths
        .map((p) => groupByNormalizedName[normalize(p)])
        .filter(Boolean) as FunnelGroup[],
    [customerLifeCyclePaths, groupByNormalizedName]
  );

  const handleSelectGroup = async (targetGroup: FunnelGroup) => {
    if (!customerId || !sellerWorkspaceId) return;

    const currentInSameFunnel = currentGroups.find(
      (g) => g.funnelId === targetGroup.funnelId
    );

    if (currentInSameFunnel?.group_type === 'automatic') {
      dispatch(
        showToast({
          canDisplay: true,
          message: 'Cannot move customer from an automatic group',
          responseStatus: 'error',
          type: ErrorVariants.DANGER,
        })
      );
      return;
    }

    try {
      if (currentInSameFunnel) {
        await moveGroupMembers({
          sellerWorkspaceId,
          body: {
            fromGroupId: currentInSameFunnel.id,
            toGroupId: targetGroup.id,
            customerIds: [customerId],
          },
        }).unwrap();
      } else {
        await addGroupMember({
          sellerWorkspaceId,
          groupId: targetGroup.id,
          body: { customerIds: [customerId] },
        }).unwrap();
      }

      const updatedPaths = [
        ...customerLifeCyclePaths.filter(
          (p) =>
            groupByNormalizedName[normalize(p)]?.funnelId !==
            targetGroup.funnelId
        ),
        targetGroup.name,
      ];
      updateConversations({
        ...conversation,
        customerLifeCyclePaths: updatedPaths,
      });

      // Ensure the conversation list reflects backend truth immediately.
      // (move/add might affect lifecycle assignment fields beyond paths)
      safeRefetchConversationList();
      onClose();
    } catch (err: any) {
      const message = err?.data?.message || 'Failed to update lifecycle group';
      onClose();
      dispatch(
        showToast({
          canDisplay: true,
          message,
          responseStatus: 'error',
          type: ErrorVariants.DANGER,
        })
      );
    }
  };

  const manualGroups = useMemo(
    () => allGroups.filter((g) => g.group_type === 'manual'),
    [allGroups]
  );

  const items: ModalContentItemData[] = useMemo(
    () =>
      manualGroups.map((group) => {
        const isSelected = currentGroups.some((c) => c.id === group.id);
        return {
          icon: (
            <span style={{ fontSize: 16, lineHeight: 1, minWidth: 20 }}>
              {group.icon}
            </span>
          ),
          title: startCase(camelCase(group.name)),
          description: group.funnelNames.join(', '),
          onPress: () => handleSelectGroup(group),
          isSelected,
        };
      }),
    [manualGroups, currentGroups]
  );

  return (
    <ConversationUpdateModal
      open={open}
      onClose={onClose}
      header="Change Life Cycle Group"
      customerName={channelName || title}
    >
      {isLoading ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#8c8c8c' }}>
          Loading groups...
        </div>
      ) : (
        <ModalContentsList data={items} showSelection={true} />
      )}
    </ConversationUpdateModal>
  );
};
